import React, { useEffect, useState } from "react";
import Fuse from "fuse.js";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Search, Trash2, XCircle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { SqlScriptJobRequest } from "../../models/Jobs/JobRequest";
import {
    JobRequestStatus,
    JobRequestStatusLabels,
} from "../../models/Jobs/JobRequestStatus";
import JobRequestStatusChip from "./JobRequestStatusChip";
import AbortJobModal from "./AbortJobModal";
import JobsRepository from "../../repositories/JobsRepository";
import { QUERY_KEYS } from "../../constants/QueryKeys";
import TablePagination from "../common/Table/TablePagination";
import { useTableHandlers } from "../common/Table/Hooks/UseTableHandlers";
import {
    FilterCombobox,
    FilterComboboxOption,
} from "../common/FilterCombobox";
import { JobRequestStatusColors, JobRequestStatusIcons } from "../../models/Jobs/JobRequestStatus";
import {
  fileFiltersBoxClassName,
  filterComboboxClassName,
  filterSearchClassName,
} from "../common/Table/TableStyles";

const ACTIVE_STATUSES: JobRequestStatus[] = [
    JobRequestStatus.New,
    JobRequestStatus.Pending,
    JobRequestStatus.Processing,
];

interface SqlScriptJobsPanelProps {
    organizationId?: string;
}

const statusFilterOptions: FilterComboboxOption[] = Object.values(
    JobRequestStatus
).map((status) => ({
    value: status,
    label: JobRequestStatusLabels[status],
    color: JobRequestStatusColors[status],
    icon: JobRequestStatusIcons[status],
}));

const SqlScriptJobsPanel: React.FC<SqlScriptJobsPanelProps> = ({
    organizationId,
}) => {
    const jobsRepository = new JobsRepository();
    const queryClient = useQueryClient();
    const [jobToAbort, setJobToAbort] = useState<SqlScriptJobRequest | null>(
        null
    );
    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState<JobRequestStatus | null>(
        null
    );

    const { page, rowsPerPage, rows, setPage, setRows, handleRowsPerPageChange } =
        useTableHandlers({ page: 0, rowsPerPage: 10 });

    const jobsQuery = useQuery({
        queryKey: [QUERY_KEYS.SQL_SCRIPT_JOBS, organizationId],
        queryFn: () =>
            jobsRepository.getSqlScriptExecutionStatus(organizationId!),
        enabled: !!organizationId,
        refetchInterval: (query) =>
            query.state.data?.some((j) =>
                ACTIVE_STATUSES.includes(j.status)
            )
                ? 30000
                : false,
        refetchIntervalInBackground: true,
    });

    const sortedJobs = [...(jobsQuery.data ?? [])].sort(
        (a, b) => Number(b.id) - Number(a.id)
    );

    const statusFilteredJobs = statusFilter
        ? sortedJobs.filter((job) => job.status === statusFilter)
        : sortedJobs;

    const searchOptions = {
        keys: ["sqlScript", "reason", "submittedByUserName"],
        includeScore: true,
        threshold: 0.1,
        caseSensitive: false,
    };

    const fuse = new Fuse(statusFilteredJobs, searchOptions);

    const filteredJobs = searchQuery
        ? fuse.search(searchQuery).map((result) => result.item)
        : statusFilteredJobs;

    useEffect(() => {
        setRows(filteredJobs.length);
    }, [filteredJobs.length]);

    const paginatedJobs = filteredJobs.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    return (
        <>
            <Card className="flex flex-col overflow-hidden min-h-0 p-4">
                <div className="flex justify-between items-start mb-3">
                    <div>
                        <h3 className="text-sm font-semibold">
                            SQL Script Jobs
                        </h3>
                        <p className="text-xs text-muted-foreground">
                            SQL scripts submitted for execution against the
                            selected organization's database.
                        </p>
                    </div>
                    {jobsQuery.isFetching && (
                        <p className="text-xs text-muted-foreground">
                            Refreshing...
                        </p>
                    )}
                </div>

                <Separator className="mb-3" />

                <div className={fileFiltersBoxClassName}>
                    <div className={filterSearchClassName}>
                        <Label className="text-xs font-semibold text-muted-foreground">Search scripts</Label>
                        <div className="relative">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                            <Input
                                placeholder="Script, reason, or user"
                                className="pl-8 text-sm"
                                onChange={(e) => {
                                    setPage(0);
                                    setSearchQuery(e.target.value);
                                }}
                            />
                        </div>
                    </div>
                    <div className={filterComboboxClassName}>
                        <Label className="text-xs font-semibold text-muted-foreground">Status</Label>
                        <FilterCombobox
                            value={statusFilter}
                            onChange={(newValue) => {
                                setPage(0);
                                setStatusFilter(
                                    newValue as JobRequestStatus | null
                                );
                            }}
                            options={statusFilterOptions}
                            placeholder="Status"
                        />
                    </div>
                </div>

                <div className="overflow-y-auto flex-1">
                    {jobsQuery.isLoading ? (
                        <div className="space-y-2">
                            {[1, 2, 3].map((i) => (
                                <Skeleton
                                    key={i}
                                    className="h-[88px] rounded-md"
                                />
                            ))}
                        </div>
                    ) : filteredJobs.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center mt-8">
                            {sortedJobs.length === 0
                                ? "No SQL script jobs found."
                                : "No jobs match the current filters."}
                        </p>
                    ) : (
                        <div className="space-y-2">
                            {paginatedJobs.map((job) => (
                                <SqlScriptJobCard
                                    key={job.id}
                                    job={job}
                                    onAbortClick={() => setJobToAbort(job)}
                                />
                            ))}
                        </div>
                    )}
                </div>

                <TablePagination
                    page={page}
                    rowsPerPage={rowsPerPage}
                    rows={rows}
                    onPageChange={setPage}
                    onRowsPerPageChange={handleRowsPerPageChange}
                />
            </Card>

            {jobToAbort && (
                <AbortJobModal
                    open
                    jobRequest={jobToAbort}
                    onClose={() => setJobToAbort(null)}
                    onConfirm={async () => {
                        await jobsRepository.deleteJob(jobToAbort.id);
                        await queryClient.invalidateQueries({
                            queryKey: [
                                QUERY_KEYS.SQL_SCRIPT_JOBS,
                                organizationId,
                            ],
                        });
                        setJobToAbort(null);
                    }}
                />
            )}
        </>
    );
};

interface SqlScriptJobCardProps {
    job: SqlScriptJobRequest;
    onAbortClick: () => void;
}

const SqlScriptJobCard: React.FC<SqlScriptJobCardProps> = ({
    job,
    onAbortClick,
}) => {
    const canAbort =
        job.status === JobRequestStatus.Pending ||
        job.status === JobRequestStatus.Completed;
    const hasError =
        !!job.executionError || job.status === JobRequestStatus.Failed;

    return (
        <Card className="p-3 gap-0">
            <CardContent className="p-0">
                <div className="flex justify-between items-start mb-1">
                    <div>
                        <p className="text-sm font-semibold">
                            Organization Name: {job.organizationName}
                        </p>
                        <p className="text-xs text-muted-foreground">
                            Submitted by: {job.submittedByUserName} (
                            {job.submittedByUserId})
                        </p>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <JobRequestStatusChip status={job.status} />
                        {canAbort && (
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            size="icon-sm"
                                            className="text-destructive hover:text-destructive"
                                            onClick={onAbortClick}
                                        >
                                            {job.status ===
                                            JobRequestStatus.Completed ? (
                                                <Trash2 className="size-4" />
                                            ) : (
                                                <XCircle className="size-4" />
                                            )}
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent side="left">
                                        {job.status ===
                                        JobRequestStatus.Completed
                                            ? "Delete this job record"
                                            : "Abort this job before it is processed"}
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        )}
                    </div>
                </div>

                <p className="text-xs font-medium text-muted-foreground mt-1.5 mb-0.5">
                    Script
                </p>
                <div className="p-2 bg-muted rounded-sm border font-mono text-xs whitespace-pre-wrap break-words max-h-20 overflow-auto">
                    {job.sqlScript}
                </div>

                <p className="text-xs text-muted-foreground mt-1.5 italic break-words">
                    <span className="font-medium not-italic">Reason: </span>
                    {job.reason}
                </p>

                {job.status === JobRequestStatus.Completed && !hasError && (
                    <div className="flex items-center gap-3 mt-1.5 text-xs divide-x">
                        <span className="font-medium text-muted-foreground">
                            Execution results:
                        </span>
                        {job.executionDurationMs !== undefined && (
                            <span className="pl-3">
                                Duration: {job.executionDurationMs}ms
                            </span>
                        )}
                        {job.executionAffectedRowCount !== undefined && (
                            <span className="pl-3">
                                Rows affected: {job.executionAffectedRowCount}
                            </span>
                        )}
                    </div>
                )}
                {hasError && (
                    <div className="mt-1.5 p-2 bg-muted rounded-sm border border-destructive/30 font-mono text-xs whitespace-pre-wrap break-all text-destructive max-h-20 overflow-auto">
                        {job.executionError ??
                            "The job failed unexpectedly."}
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export default SqlScriptJobsPanel;
